import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./TimePicker.module.css";
import TextInput from "shared/@forms/TextInput";
import { Time } from "react-ikonate";
import { Modal } from "shared/Modal";
import { HoursList } from "Events/Components/TimePicker/HoursList/HoursList";
import moment from "moment";
import { IoIosCloseCircle } from "react-icons/io";
import { IconContext } from "react-icons";

/**
 * Interactive component. Allows the user to type in the time and toggle am/pm
 */
export function TimePicker({ timeString, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localTimeString, setLocalTimeString] = useState("");
  const [localTimeStringManual, setLocalTimeStringManual] = useState("");
  const [isManualMode, setIsManualMode] = useState(false); // track if user is typing
  const [isAM, setIsAM] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const handleMouseDown = (e) => {
    setIsDragging(true);
    // Calculate the offset between the mouse position and the modal's top-left corner
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  useEffect(() => {
    if (timeString) {
      setLocalTimeString(timeString);
    }
  }, [timeString]);

  useEffect(() => {
    setIsAM(getAMPM(localTimeString));
    if (!isManualMode) {
      setLocalTimeStringManual(convertTo12HourFormat(localTimeString));
    }
  }, [localTimeString, isManualMode]);

  const timeStringToArray = (time) => {
    if (!time || typeof time !== "string") {
      return [0, 0];
    }

    const momentTime = moment(time, "HH:mm");
    return [momentTime.hour(), momentTime.minute()];
  };

  const getAMPM = (time) => {
    if (typeof time !== "string" || time === "") return true;
    const [hourStr] = time.split(":");
    const hour = parseInt(hourStr, 10) % 24;
    return !(hour >= 12 && hour < 24);
  };

  const toggleAMPM = () => {
    if (!localTimeString) return;

    const [hour, minute] = timeStringToArray(localTimeString);
    let newHour = isAM ? hour + 12 : hour - 12;

    if (newHour < 0) newHour += 24;
    if (newHour > 23) newHour -= 24;

    const formattedTime = `${String(newHour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}`;
    onChange(formattedTime);
  };

  const convertTo12HourFormat = (time) => {
    if (!time || typeof time !== "string") return "";
    const [hour, minute] = time.split(":").map(Number);
    let displayHour = hour % 12;
    if (displayHour === 0) displayHour = 12;
    return `${displayHour}:${String(minute).padStart(2, "0")}`;
  };

  const handleTimeSelect = (value) => {
    const [selectedHour, selectedMinute] = value;
    setIsAM(selectedHour < 12);

    const formattedTime = `${String(selectedHour).padStart(2, "0")}:${String(
      selectedMinute
    ).padStart(2, "0")}`;

    setLocalTimeString(formattedTime);
    setIsOpen(false);
    setIsManualMode(false);
    onChange(formattedTime);
  };

  const handleManualChange = (e) => {
    setIsManualMode(true);
    setLocalTimeStringManual(e.target.value);
  };

  const handleManualTimeConfirm = () => {
    let input = localTimeStringManual.trim().toUpperCase().replace(/\s+/g, "");

    // Handle compact input like "130PM" or "0730"
    const colonlessMatch = input.match(/^(\d{1,2})(\d{2})(AM|PM)?$/);
    if (colonlessMatch) {
      const [, hour, minute, period] = colonlessMatch;
      input = `${parseInt(hour, 10)}:${minute}${period ? ` ${period}` : ""}`;
    }

    let parsed = moment(input, ["h:mm A", "hh:mm A", "H:mm", "HH:mm"], true);

    // If parsed, use it
    if (parsed.isValid()) {
      // If input didn't include AM/PM but is ambiguous (like 7:30), adjust based on current isAM
      if (!/[AP]M/.test(input) && parsed.hour() < 12 && !isAM) {
        parsed = parsed.add(12, "hours");
      }

      const formatted = parsed.format("HH:mm");
      setLocalTimeString(formatted);
      setLocalTimeStringManual(convertTo12HourFormat(formatted));
      onChange(formatted); // Notify parent
    } else {
      // If invalid, revert to last valid state and re-send it to parent
      setLocalTimeStringManual(convertTo12HourFormat(localTimeString));
      onChange(localTimeString);
    }

    setIsManualMode(false);
  };

  const handleManualTimeBlur = () => {
    handleManualTimeConfirm();
  };

  const handleManualTimeKeyDown = (e) => {
    if (e.key === "Enter") {
      handleManualTimeConfirm();
    }
  };

  return (
    <div className={styles.tpContainer}>
      <div className={styles.timeIcon}>
        <Time fontSize={12} onClick={() => setIsOpen(!isOpen)} />
      </div>

      <TextInput
        className={styles.tpInput}
        value={localTimeStringManual}
        placeholder="12:00"
        maxLength="7"
        onChange={handleManualChange}
        onBlur={handleManualTimeBlur}
        onKeyDown={handleManualTimeKeyDown}
      />

      <div className={styles.ampmButtonContainer}>
        <button
          className={styles.amButton}
          style={
            isAM
              ? { backgroundColor: "#F4C579" }
              : { backgroundColor: "transparent" }
          }
          onClick={toggleAMPM}
        >
          AM
        </button>
        <button
          className={styles.pmButton}
          style={
            !isAM
              ? { backgroundColor: "#9AB5BB" }
              : { backgroundColor: "transparent" }
          }
          onClick={toggleAMPM}
        >
          PM
        </button>
      </div>

      {isOpen && (
        <div className={styles.overlay}>
          <div
            className={styles.modal}
            style={{
              transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div className={styles.header}>
              <span className={styles.title}>CHOOSE TIME</span>
              <IconContext.Provider
                value={{
                  size: "24px",
                }}
              >
                <IoIosCloseCircle
                  onClick={() => setIsOpen(false)}
                  className={styles.closeButton}
                />
              </IconContext.Provider>
            </div>
            <div className={styles.popOverMenu}>
              <section className={styles.section}>
                <header>
                  <h3>Day</h3>
                </header>
                <HoursList
                  value={timeStringToArray(localTimeString)}
                  startHour={6}
                  onSelect={handleTimeSelect}
                />
              </section>
              <section className={`${styles.section} ${styles.night}`}>
                <header>
                  <h3>Night</h3>
                </header>
                <HoursList
                  value={timeStringToArray(localTimeString)}
                  startHour={18}
                  onSelect={handleTimeSelect}
                />
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TimePicker.propTypes = {
  timeString: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
